function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
// ----------------------------------------------------------------------------
// Product Form Extension: Variant Dropdown Price Differential
// ----------------------------------------------------------------------------
// Annotates each <option> in the variant dropdown(s) with its price difference
// relative to the *currently selected* variant.
//
//   Polished              ← currently selected (base, no suffix)
//   Satin (+$25.99)       ← $25.99 more than the selected variant
//
// The differential is recomputed on every variant change, so once a more
// expensive option is selected the cheaper options flip to negative:
//
//   Polished (-$25.99)
//   Satin                 ← now the selected base
// ----------------------------------------------------------------------------
// Scope:
// - Only the <select> dropdowns (`.single-option-selector`) are annotated.
//   Swatch-style options (color/text) are left untouched.
// - For multi-option products, an option's price is resolved by holding every
//   *other* currently-selected option fixed and swapping in the candidate
//   value, then matching the resulting variant. If no such variant exists,
//   no suffix is shown.
// ----------------------------------------------------------------------------
// Event Listeners:
// - 'productForm:init'            → Instantiate the helper and render the
//                                   initial differentials
// - 'productForm:variant:changed' → Re-render differentials relative to the
//                                   newly selected variant
// ----------------------------------------------------------------------------
// Key Data Mutations:
// - Rewrites the `textContent` of dropdown <option> elements only. The option
//   `value` is never touched, so Shopify's variant selection logic is unaffected.
// ----------------------------------------------------------------------------
// Gotchas:
// - Reads `variant.price` (in cents) from `iamForm.productData.variants`; it
//   intentionally ignores line-item property surcharges (see
//   product-form--property-pricing) since those are not variant-driven.
// - The original option label is cached on `data-base-label` the first time an
//   option is seen, so repeated renders never compound the suffix.
// ----------------------------------------------------------------------------

// -------------------------------------
// Dependencies
// -------------------------------------

import { formatMoney } from '@shopify/theme-currency';
import iamotaEventHandler from 'scripts/helpers/iamota-event-handler';
import Debug from 'scripts/libs/debug';

// -------------------------------------
// Functionality
// -------------------------------------

class VariantPriceDiff {
  constructor(iamProductForm) {
    this.iamForm = iamProductForm;
    this.render = this.render.bind(this);

    // Recompute differentials whenever the selected variant changes, since
    // the differentials are always relative to the current selection.
    iamotaEventHandler.subscribe('productForm:variant:changed', this.render, {
      context: this.iamForm,
      source: "product-form--variant-price-diff.js"
    });

    // Render the initial state.
    this.render();
  }

  /**
   * Annotate every dropdown option with its price difference vs. the
   * currently selected variant.
   */
  render() {
    var _this$iamForm$product;
    var currentVariant = this.iamForm.getCurrentVariant();
    var currentOptions = this.iamForm.getCurrentOptions();
    var variants = (_this$iamForm$product = this.iamForm.productData) === null || _this$iamForm$product === void 0 ? void 0 : _this$iamForm$product.variants;
    if (!currentVariant || !currentOptions || !variants) return;
    this.iamForm.querySelectorAll(VariantPriceDiff.DROPDOWN_SELECTOR).forEach(select => {
      var position = this.getOptionPosition(select);
      if (!Number.isFinite(position)) return;
      Array.from(select.options).forEach(optionEl => {
        var baseLabel = this.getBaseLabel(optionEl);
        var candidate = this.findVariantForOption(variants, currentOptions, position, optionEl.value);
        var diff = candidate ? candidate.price - currentVariant.price : 0;
        optionEl.textContent = diff === 0 ? baseLabel : "".concat(baseLabel, " (").concat(this.formatDiff(diff), ")");
      });
    });
    Debug.details('[variant-price-diff] render', {
      currentVariantId: currentVariant.id,
      currentPrice: currentVariant.price
    });
  }

  /**
   * Resolve the variant produced by swapping a single option value while
   * keeping all other currently-selected options fixed.
   * @param {Array} variants - All product variants (from productData)
   * @param {Array} currentOptions - The currently selected options ([{ value }])
   * @param {Number} position - The 1-based option position being varied
   * @param {String} value - The candidate option value
   * @returns {Object|null} The matching variant, or null if none exists
   */
  findVariantForOption(variants, currentOptions, position, value) {
    var desired = currentOptions.map(option => option.value);
    desired[position - 1] = value;
    return variants.find(variant => desired.every((val, index) => variant["option".concat(index + 1)] === val)) || null;
  }

  /**
   * Format a price difference (in cents) as a signed money string,
   * e.g. 2599 → "+$25.99", -2599 → "-$25.99".
   * @param {Number} diff - The price difference in cents
   * @returns {String}
   */
  formatDiff(diff) {
    var sign = diff > 0 ? '+' : '-';
    var amount = formatMoney(Math.abs(diff), window.theme.settingCurrencyFormat || '{{amount}}');
    return "".concat(sign).concat(amount);
  }

  /**
   * Read (and cache on first use) the original, un-suffixed option label so
   * repeated renders never compound the differential suffix.
   * @param {HTMLOptionElement} optionEl
   * @returns {String}
   */
  getBaseLabel(optionEl) {
    if (!optionEl.dataset.baseLabel) {
      optionEl.dataset.baseLabel = optionEl.textContent.trim();
    }
    return optionEl.dataset.baseLabel;
  }

  /**
   * Determine the 1-based option position for a dropdown, from its
   * `data-index="option{n}"` attribute (falling back to its id).
   * @param {HTMLSelectElement} select
   * @returns {Number}
   */
  getOptionPosition(select) {
    var fromIndex = Number.parseInt(String(select.dataset.index || '').replace(/\D/g, ''), 10);
    if (Number.isFinite(fromIndex)) return fromIndex;
    return Number.parseInt(String(select.id || '').replace(/\D/g, ''), 10);
  }
}

// -------------------------------------
// Initialization
// -------------------------------------
_defineProperty(VariantPriceDiff, "DROPDOWN_SELECTOR", 'iam-variant-picker select.single-option-selector');
iamotaEventHandler.subscribe('productForm:init', _ref => {
  var {
    iamForm
  } = _ref;
  new VariantPriceDiff(iamForm);
}, {
  source: "product-form--variant-price-diff.js"
});